(function () {
  const revealSelector = [
    ".nova-section",
    ".nova-dark-story",
    ".nova-product-detail-hero",
    ".nova-group-hero-content",
    ".nova-contact-band",
    ".nova-contact-form",
    ".nova-hero-copy",
    ".nova-heading-lg",
    ".nova-story-copy",
    ".nova-dark-story-header",
    ".nova-stat-row",
    ".nova-proof-card",
    ".nova-about-hero-copy",
    ".nova-about-copy",
    ".nova-about-kicker",
    ".nova-about-section h2",
    ".nova-about-split > div:first-child",
    ".nova-about-panel > div",
    ".nova-about-panel",
    ".nova-about-section",
    ".nova-about-split",
    ".nova-about-quotes",
    ".nova-footer",
    ".nova-product-tile",
    ".nova-story-card",
    ".nova-blog-card",
    ".nova-quick-card",
    ".nova-bulk-card",
    ".nova-faq-item",
    ".nova-service-card",
    ".nova-compare-card",
    ".nova-hero-copy > *",
    ".nova-story-copy > *",
    ".nova-proof-card > *",
    ".nova-about-hero-copy > *",
    ".nova-about-split > div:first-child > *",
    ".nova-about-panel > div > *",
    ".content-for-layout :is(h1, h2, h3, h4, h5, h6, p, blockquote, li, dt, dd, summary, .nova-pill, .nova-link-pill)"
  ].join(",");

  const staggerGroups = [
    ".nova-product-row",
    ".nova-card-grid",
    ".nova-story-grid",
    ".nova-blog-grid",
    ".nova-quick-grid",
    ".nova-bulk-card-grid",
    ".nova-service-row",
    ".nova-compare-grid",
    ".nova-footer-links",
    ".nova-footer-contact",
    ".nova-hero-copy",
    ".nova-story-copy",
    ".nova-about-hero-copy",
    ".nova-about-split > div:first-child",
    ".nova-about-panel > div",
    ".nova-proof-card"
  ];

  let observer;
  let counterObserver;
  let sectionNavFrame;
  let aboutOpeningFrame;
  const animatedCounters = new WeakSet();
  const horizontalScrollbarTimers = new WeakMap();
  const horizontalScrollbarListeners = new WeakSet();

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function applyStagger(root) {
    staggerGroups.forEach((selector) => {
      root.querySelectorAll(selector).forEach((group) => {
        Array.from(group.children).forEach((child, index) => {
          if (child instanceof HTMLElement) {
            child.style.setProperty("--reveal-delay", `${Math.min(index, 7) * 70}ms`);
          }
        });
      });
    });
  }

  function initReveal() {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isHomepage = document.body.classList.contains("template-index");
    const targets = Array.from(document.querySelectorAll(revealSelector)).filter((element) => {
      return (
        element instanceof HTMLElement &&
        !element.closest(".nova-nav") &&
        !element.matches("[data-no-scroll-reveal]") &&
        !element.closest("[data-no-scroll-reveal]") &&
        !element.matches(".visually-hidden, [hidden]") &&
        !element.closest(".visually-hidden, [hidden], #header-group") &&
        !(isHomepage && element.closest(".nova-hero")) &&
        !element.matches("[data-about-opening-copy], [data-about-opening-intro]")
      );
    });

    applyStagger(document);

    targets.forEach((element) => {
      element.setAttribute("data-scroll-reveal", "");
    });

    if (reduceMotion || !("IntersectionObserver" in window)) {
      targets.forEach((element) => element.classList.add("is-visible"));
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              observer.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: "0px 0px -12% 0px",
          threshold: 0.12
        }
      );
    }

    targets.forEach((element) => {
      if (!element.classList.contains("is-visible")) {
        observer.observe(element);
      }
    });
  }

  function initHorizontalScrollbars() {
    document.querySelectorAll("body *").forEach((element) => {
      if (!(element instanceof HTMLElement) || element.scrollWidth <= element.clientWidth + 1) {
        return;
      }

      const overflowX = getComputedStyle(element).overflowX;
      if (overflowX !== "auto" && overflowX !== "scroll") {
        return;
      }

      element.classList.add("nova-horizontal-scrollbar");

      if (horizontalScrollbarListeners.has(element)) {
        return;
      }

      horizontalScrollbarListeners.add(element);
      element.addEventListener(
        "scroll",
        () => {
          element.classList.add("is-scrolling");
          window.clearTimeout(horizontalScrollbarTimers.get(element));
          horizontalScrollbarTimers.set(
            element,
            window.setTimeout(() => element.classList.remove("is-scrolling"), 700)
          );
        },
        { passive: true }
      );
    });
  }

  function getCounterParts(text) {
    const match = text.trim().match(/^([^0-9+-]*)([+-]?\d[\d,]*(?:\.\d+)?)(.*)$/);

    if (!match) {
      return null;
    }

    const rawNumber = match[2];
    const value = Number.parseFloat(rawNumber.replace(/,/g, ""));

    if (!Number.isFinite(value)) {
      return null;
    }

    const decimalPart = rawNumber.split(".")[1];

    return {
      prefix: match[1],
      value,
      suffix: match[3],
      decimals: decimalPart ? decimalPart.length : 0,
      useGrouping: rawNumber.includes(",")
    };
  }

  function formatCounterValue(value, parts) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: parts.decimals,
      maximumFractionDigits: parts.decimals,
      useGrouping: parts.useGrouping
    });
  }

  function animateCounter(element) {
    if (animatedCounters.has(element)) {
      return;
    }

    const parts = getCounterParts(element.dataset.countUpValue || element.textContent || "");

    if (!parts) {
      animatedCounters.add(element);
      return;
    }

    animatedCounters.add(element);

    const duration = 1200;
    const start = performance.now();

    function render(now) {
      const progress = clamp((now - start) / duration, 0, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = parts.value * eased;

      element.textContent = `${parts.prefix}${formatCounterValue(current, parts)}${parts.suffix}`;

      if (progress < 1) {
        window.requestAnimationFrame(render);
      } else {
        element.textContent = `${parts.prefix}${formatCounterValue(parts.value, parts)}${parts.suffix}`;
      }
    }

    element.textContent = `${parts.prefix}${formatCounterValue(0, parts)}${parts.suffix}`;
    window.requestAnimationFrame(render);
  }

  function initCounters() {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const targets = Array.from(document.querySelectorAll("[data-count-up]")).filter((element) => {
      return element instanceof HTMLElement;
    });

    targets.forEach((element) => {
      if (!element.dataset.countUpValue) {
        element.dataset.countUpValue = element.textContent || "";
      }
    });

    if (reduceMotion || !("IntersectionObserver" in window)) {
      targets.forEach((element) => {
        animatedCounters.add(element);
      });
      return;
    }

    if (!counterObserver) {
      counterObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.target instanceof HTMLElement) {
              animateCounter(entry.target);
              counterObserver.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: "0px 0px -10% 0px",
          threshold: 0.35
        }
      );
    }

    targets.forEach((element) => {
      if (!animatedCounters.has(element)) {
        counterObserver.observe(element);
      }
    });
  }

  function updateAboutOpening() {
    aboutOpeningFrame = null;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    document.querySelectorAll("[data-about-opening]").forEach((opening) => {
      if (!(opening instanceof HTMLElement)) return;

      const intro = opening.querySelector("[data-about-opening-intro]");

      if (!(intro instanceof HTMLElement)) return;

      const progress = reduceMotion
        ? 1
        : clamp((window.innerHeight - intro.getBoundingClientRect().top) / (window.innerHeight * 0.8), 0, 1);

      opening.style.setProperty("--nova-about-opening-progress", progress.toFixed(3));
      opening.style.setProperty("--nova-about-opening-blur", `${(progress * 40).toFixed(2)}px`);
      opening.style.setProperty("--nova-about-opening-scale", (1 + progress * 0.12).toFixed(3));
    });
  }

  function requestAboutOpeningUpdate() {
    if (aboutOpeningFrame) return;

    aboutOpeningFrame = window.requestAnimationFrame(updateAboutOpening);
  }

  function updateAboutStory() {
    const threshold = window.innerHeight * 0.5;

    document.querySelectorAll("[data-about-story]").forEach((story) => {
      if (!(story instanceof HTMLElement)) return;

      const steps = Array.from(story.querySelectorAll("[data-about-story-step]"));
      const images = Array.from(story.querySelectorAll("[data-about-story-image]"));
      let activeIndex = 0;

      steps.forEach((step, index) => {
        if (step instanceof HTMLElement && step.getBoundingClientRect().top <= threshold) {
          activeIndex = index;
        }
      });

      images.forEach((image, index) => {
        if (image instanceof HTMLElement) {
          image.classList.toggle("is-active", index === activeIndex);
        }
      });
    });
  }

  function updateAboutPpt() {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    document.querySelectorAll("[data-about-ppt]").forEach((section) => {
      if (!(section instanceof HTMLElement)) return;

      const slides = Array.from(section.querySelectorAll("[data-about-ppt-slide]"));
      if (slides.length < 2) return;

      const bounds = section.getBoundingClientRect();
      const scrollableDistance = Math.max(section.offsetHeight - window.innerHeight, 1);
      const progress = clamp(-bounds.top / scrollableDistance, 0, 1);
      const activeIndex = reduceMotion || progress < 0.5 ? 0 : 1;

      slides.forEach((slide, index) => {
        if (!(slide instanceof HTMLElement)) return;
        slide.classList.toggle("is-active", index === activeIndex);
        slide.classList.toggle("is-past", index < activeIndex);
      });
    });
  }

  function initStoryMarqueeDrag() {
    document.querySelectorAll(".nova-story-grid--marquee").forEach((grid) => {
      if (!(grid instanceof HTMLElement) || grid.dataset.dragReady === "true") {
        return;
      }

      const track = grid.querySelector(".nova-story-marquee-track");
      if (!(track instanceof HTMLElement)) {
        return;
      }

      let startX = 0;
      let startOffset = 0;
      let currentOffset = 0;
      let dragging = false;

      const getLoopWidth = () => track.scrollWidth / 2;

      const normalizeOffset = (offset) => {
        const loopWidth = getLoopWidth();
        if (!loopWidth) return 0;

        let normalized = offset;
        while (normalized > 0) normalized -= loopWidth;
        while (normalized <= -loopWidth) normalized += loopWidth;
        return normalized;
      };

      const getCurrentOffset = () => {
        const transform = getComputedStyle(track).transform;
        if (!transform || transform === "none") return 0;
        return new DOMMatrixReadOnly(transform).m41;
      };

      const finishDrag = () => {
        if (!dragging) return;

        dragging = false;
        const loopWidth = getLoopWidth();
        if (loopWidth) {
          const progress = -normalizeOffset(currentOffset) / loopWidth;
          track.style.setProperty("--nova-story-animation-delay", `${-(progress * 54)}s`);
        }
        grid.classList.remove("is-dragging");
        track.style.removeProperty("--nova-story-drag-x");
      };

      grid.addEventListener("pointerdown", (event) => {
        if (event.button !== 0) return;

        event.preventDefault();
        startX = event.clientX;
        startOffset = normalizeOffset(getCurrentOffset());
        currentOffset = startOffset;
        dragging = true;
        grid.classList.add("is-dragging");
        track.style.setProperty("--nova-story-drag-x", `${currentOffset}px`);
        grid.setPointerCapture(event.pointerId);
      });

      grid.addEventListener("pointermove", (event) => {
        if (!dragging) return;

        currentOffset = normalizeOffset(startOffset + event.clientX - startX);
        track.style.setProperty("--nova-story-drag-x", `${currentOffset}px`);
      });

      grid.addEventListener("pointerup", finishDrag);
      grid.addEventListener("pointercancel", finishDrag);
      grid.addEventListener("dragstart", (event) => event.preventDefault());
      grid.dataset.dragReady = "true";
    });
  }

  function initTestimonialRailDrag() {
    document.querySelectorAll(".nova-testimonials-section--quote-grid .nova-testimonial-rail").forEach((rail) => {
      if (!(rail instanceof HTMLElement) || rail.dataset.dragReady === "true") {
        return;
      }

      let startX = 0;
      let startScrollLeft = 0;
      let dragging = false;

      const finishDrag = (event) => {
        if (!dragging) return;

        dragging = false;
        rail.classList.remove("is-dragging");
        rail.releasePointerCapture(event.pointerId);
      };

      rail.addEventListener("pointerdown", (event) => {
        if (event.button !== 0 || rail.scrollWidth <= rail.clientWidth) return;

        startX = event.clientX;
        startScrollLeft = rail.scrollLeft;
        dragging = true;
        rail.classList.add("is-dragging");
        rail.setPointerCapture(event.pointerId);
      });

      rail.addEventListener("pointermove", (event) => {
        if (!dragging) return;

        rail.scrollLeft = startScrollLeft - (event.clientX - startX);
      });

      rail.addEventListener("pointerup", finishDrag);
      rail.addEventListener("pointercancel", finishDrag);
      rail.dataset.dragReady = "true";
    });
  }

  function initHorizontalDragScroll() {
    document.querySelectorAll("[data-drag-scroll]").forEach((scroller) => {
      if (!(scroller instanceof HTMLElement) || scroller.dataset.dragReady === "true") {
        return;
      }

      let startX = 0;
      let startScrollLeft = 0;
      let dragging = false;
      let hasDragged = false;

      const finishDrag = (event) => {
        if (!dragging) return;

        dragging = false;
        scroller.classList.remove("is-dragging");
        if (scroller.hasPointerCapture(event.pointerId)) {
          scroller.releasePointerCapture(event.pointerId);
        }
      };

      scroller.addEventListener("pointerdown", (event) => {
        if (event.button !== 0 || scroller.scrollWidth <= scroller.clientWidth) return;

        startX = event.clientX;
        startScrollLeft = scroller.scrollLeft;
        dragging = true;
        hasDragged = false;
      });

      scroller.addEventListener("pointermove", (event) => {
        if (!dragging) return;

        const distance = event.clientX - startX;
        if (Math.abs(distance) <= 4 && !hasDragged) return;

        if (!hasDragged) {
          hasDragged = true;
          scroller.classList.add("is-dragging");
          scroller.setPointerCapture(event.pointerId);
        }

        event.preventDefault();
        scroller.scrollLeft = startScrollLeft - distance;
      });

      scroller.addEventListener("pointerup", finishDrag);
      scroller.addEventListener("pointercancel", finishDrag);
      scroller.addEventListener("click", (event) => {
        if (!hasDragged) return;

        event.preventDefault();
        event.stopPropagation();
        hasDragged = false;
      }, true);
      scroller.addEventListener("dragstart", (event) => event.preventDefault());
      scroller.dataset.dragReady = "true";
    });
  }

  function updatePdpSectionNav() {
    sectionNavFrame = null;

    const v1Nav = document.querySelector("[data-v1-pdp-section-nav]");
    if (v1Nav instanceof HTMLElement && v1Nav.parentElement instanceof HTMLElement) {
      const headerHeight = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
      v1Nav.classList.toggle("is-stuck", v1Nav.parentElement.getBoundingClientRect().top <= headerHeight);
    }

    document.querySelectorAll('[data-pdp-section-nav]:not([data-v1-pdp-section-nav])').forEach((sectionNav) => {
      if (!(sectionNav instanceof HTMLElement) || !sectionNav.parentElement?.classList.contains('nova-pdp-section-nav-slot')) return;

      const headerHeight = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      sectionNav.classList.toggle('is-stuck', sectionNav.parentElement.getBoundingClientRect().top <= headerHeight);
    });

    const nav = document.querySelector("[data-pdp-section-nav]");

    if (!(nav instanceof HTMLElement)) {
      return;
    }

    const links = Array.from(nav.querySelectorAll('a[href^="#"]')).filter((link) => {
      return link instanceof HTMLAnchorElement;
    });

    const targets = links
      .map((link) => {
        const id = decodeURIComponent(link.hash.slice(1));
        const section = id ? document.getElementById(id) : null;

        return section instanceof HTMLElement ? { link, section } : null;
      })
      .filter(Boolean);

    if (!targets.length) {
      return;
    }

    const navHeight = nav.offsetHeight;
    const headerHeight = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
    const activationY = headerHeight + navHeight + Math.min(160, window.innerHeight * 0.25);
    let active = targets[0];

    targets.forEach((target) => {
      const rect = target.section.getBoundingClientRect();

      if (rect.top <= activationY && rect.bottom > headerHeight + navHeight) {
        active = target;
      }
    });

    const previousActive = nav.querySelector("a.is-active");

    links.forEach((link) => {
      const isActive = link === active.link;
      link.classList.toggle("is-active", isActive);
      if (isActive) {
        link.setAttribute("aria-current", "true");
      } else {
        link.removeAttribute("aria-current");
      }
    });

    const currentLabel = nav.querySelector('[data-pdp-section-nav-current]');
    if (currentLabel) {
      currentLabel.textContent = active.link.textContent?.trim() || '';
    }

    if (previousActive !== active.link) {
      const navScroller = active.link.closest("nav");
      if (navScroller instanceof HTMLElement) {
        const linkRect = active.link.getBoundingClientRect();
        const scrollerRect = navScroller.getBoundingClientRect();
        const offset = linkRect.left - scrollerRect.left - (scrollerRect.width - linkRect.width) / 2;
        navScroller.scrollBy({ left: offset, behavior: "smooth" });
      }
    }
  }

  function requestPdpSectionNavUpdate() {
    if (sectionNavFrame) {
      return;
    }

    sectionNavFrame = window.requestAnimationFrame(updatePdpSectionNav);
  }

  function initPdpSectionNavToggle() {
    document.querySelectorAll('[data-pdp-section-nav]').forEach((nav) => {
      if (!(nav instanceof HTMLElement) || nav.dataset.pdpNavToggleReady) return;
      const toggle = nav.querySelector('[data-pdp-section-nav-toggle]');
      if (!(toggle instanceof HTMLButtonElement)) return;

      toggle.addEventListener('click', () => {
        const isOpen = nav.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(isOpen));
      });
      nav.querySelectorAll('a[href^="#"]').forEach((link) => {
        link.addEventListener('click', () => {
          nav.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
        });
      });
      nav.dataset.pdpNavToggleReady = 'true';
    });
  }

  function initScrollEffects() {
    initReveal();
    initCounters();
    initStoryMarqueeDrag();
    initTestimonialRailDrag();
    initHorizontalDragScroll();
    initPdpSectionNavToggle();
    updatePdpSectionNav();
    updateAboutOpening();
    updateAboutStory();
    updateAboutPpt();

    const scheduleHorizontalScrollbarScan = () => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(initHorizontalScrollbars, { timeout: 3000 });
      } else {
        window.setTimeout(initHorizontalScrollbars, 1000);
      }
    };

    scheduleHorizontalScrollbarScan();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initScrollEffects, { once: true });
  } else {
    initScrollEffects();
  }

  window.addEventListener("scroll", requestPdpSectionNavUpdate, { passive: true });
  window.addEventListener("scroll", requestAboutOpeningUpdate, { passive: true });
  window.addEventListener("scroll", updateAboutStory, { passive: true });
  window.addEventListener("scroll", updateAboutPpt, { passive: true });
  document.querySelector(".page-wrapper")?.addEventListener("scroll", requestPdpSectionNavUpdate, { passive: true });
  document.querySelector(".page-wrapper")?.addEventListener("scroll", requestAboutOpeningUpdate, { passive: true });
  document.querySelector(".page-wrapper")?.addEventListener("scroll", updateAboutStory, { passive: true });
  document.querySelector(".page-wrapper")?.addEventListener("scroll", updateAboutPpt, { passive: true });
  window.addEventListener("resize", requestPdpSectionNavUpdate);
  window.addEventListener("resize", requestAboutOpeningUpdate);
  window.addEventListener("resize", updateAboutStory);
  window.addEventListener("resize", updateAboutPpt);
  window.addEventListener("resize", initHorizontalScrollbars);

  document.addEventListener("novamatrix:content-ready", initScrollEffects);
  document.addEventListener("shopify:section:load", initScrollEffects);
})();
